import { apiFetch } from './api';
import type { SubscriptionStatus } from './billing';

// ── Organization ────────────────────────────────────────────────────────────

export type OrganizationDto = {
  id: number;
  name: string;
  timezone: string;
  plan: { id: number; name: string } | null;
  subscriptionStatus: SubscriptionStatus;
  /**
   * Whether this org may see and set up the partner callback (with owner role).
   * Access only — it does not mean an integration exists; access without one is
   * the normal "not set up yet" state. The API stays OWNER-guarded regardless.
   */
  partnerIntegrationAccess: boolean;
  createdAt: string;
};

export async function getOrganization(): Promise<OrganizationDto> {
  return apiFetch<OrganizationDto>('/settings/organization');
}

export async function updateOrganization(name: string): Promise<OrganizationDto> {
  return apiFetch<OrganizationDto>('/settings/organization', {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export async function updateOrganizationTimezone(
  timezone: string,
): Promise<OrganizationDto> {
  return apiFetch<OrganizationDto>('/settings/organization/timezone', {
    method: 'PATCH',
    body: JSON.stringify({ timezone }),
  });
}

// ── Workspaces ──────────────────────────────────────────────────────────────

export type WorkspaceDto = {
  id: number;
  name: string;
  createdAt: string;
  /**
   * The caller's role in this workspace. Optional only because older list
   * responses may omit it — an OWNER is no longer the exception it once was:
   * owners hold a real `owner` row in every workspace, so this comes back
   * populated for them too.
   */
  role?: WorkspaceRole;
  quota?: { dailyWarmupBudget: number };
};

export async function getWorkspaces(): Promise<WorkspaceDto[]> {
  return apiFetch<WorkspaceDto[]>('/settings/workspaces');
}

export async function createWorkspace(
  name: string,
  dailyWarmupBudget?: number,
): Promise<WorkspaceDto> {
  return apiFetch<WorkspaceDto>('/settings/workspaces', {
    method: 'POST',
    body: JSON.stringify({ name, dailyWarmupBudget }),
  });
}

export async function updateWorkspace(wsId: number, name: string): Promise<WorkspaceDto> {
  return apiFetch<WorkspaceDto>(`/settings/workspaces/${wsId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export async function deleteWorkspace(wsId: number): Promise<void> {
  await apiFetch<void>(`/settings/workspaces/${wsId}`, { method: 'DELETE' });
}

// ── OAuth helpers ───────────────────────────────────────────────────────────

export async function getGmailAuthUrl(): Promise<string> {
  const { redirectUrl } = await apiFetch<{ redirectUrl: string }>('/email-accounts/oauth/gmail/authorize');
  return redirectUrl;
}

/**
 * `type` selects the Microsoft tenant kind: `personal` (Outlook.com) or
 * `ms365` (work/school). The backend authorize endpoint accepts it as a query
 * param (`email-accounts.controller.ts`); both map onto the same OAuth flow.
 */
export async function getOutlookAuthUrl(
  type: 'personal' | 'ms365' = 'personal',
): Promise<string> {
  const { redirectUrl } = await apiFetch<{ redirectUrl: string }>(
    `/email-accounts/oauth/outlook/authorize?type=${type}`,
  );
  return redirectUrl;
}

// ── Team Members ────────────────────────────────────────────────────────────

// Workspace roles are `admin | editor | viewer` — `admin` is the ceiling.
// Company-wide ownership is `role === 'owner'` — see `ProfileDto.role`.
/**
 * The ONE role ladder, mirroring `WorkspaceRole` in `@trulyinbox/types`.
 * `owner` is company-wide (every workspace + billing / org settings / workspace
 * lifecycle); the other three are per-workspace. `OrgRole` is gone — see
 * docs/plans/2026-09-08-workspace-rbac-and-plan-restrictions.md.
 */
export type WorkspaceRole = 'owner' | 'admin' | 'editor' | 'viewer';
// Admin is invitable (the server gates assigning/inviting admin on org role).
export type InvitableRole = WorkspaceRole;

export type MemberDto = {
  userId: number;
  email: string;
  name: string | null;
  role: WorkspaceRole;
  joinedAt: string;
  /** True when this member is an org owner. Owner rows render as a locked
   * "Owner" chip in the workspace Team list — no role select, no remove. */
  isOrgOwner: boolean;
};

// ── Organization members (owner-only) ────────────────────────────────────────

export type OrgMemberDto = {
  userId: number;
  name: string | null;
  email: string;
  /**
   * The member's strongest role across the org's workspaces. `owner` is
   * all-or-nothing, so it means company-wide ownership.
   */
  role: WorkspaceRole;
  workspaces: Array<{ id: number; name: string; role: WorkspaceRole }>;
};

/** Demote body: the member becomes Admin of the ONE chosen workspace and loses
 * every other membership. Promote body carries `role: 'owner'`. */
export type UpdateOrgMemberRoleBody =
  | { role: 'owner' }
  | { role: 'admin'; workspaceId: number };

export type InviteDto = {
  id: number;
  email: string;
  role: WorkspaceRole;
  expiresAt: string;
  inviterName: string | null;
};

export type ShareableLinkDto = {
  id: number;
  role: WorkspaceRole;
  /** Raw token secret; the FE builds the accept URL from it. `null` for legacy
   * rows that predate token encryption — those rows are not re-copyable. */
  token: string | null;
  usageCount: number;
  createdByName: string | null;
  createdAt: string;
};

export async function listMembers(wsId: number): Promise<MemberDto[]> {
  return apiFetch<MemberDto[]>(`/settings/workspaces/${wsId}/members`);
}

export async function inviteMember(
  wsId: number,
  email: string,
  role: InvitableRole,
): Promise<InviteDto> {
  return apiFetch<InviteDto>(`/settings/workspaces/${wsId}/invite`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

export async function listPendingInvites(wsId: number): Promise<InviteDto[]> {
  return apiFetch<InviteDto[]>(`/settings/workspaces/${wsId}/invites`);
}

export async function resendInvite(wsId: number, inviteId: number): Promise<void> {
  await apiFetch<void>(`/settings/workspaces/${wsId}/invites/${inviteId}/resend`, { method: 'POST' });
}

export async function revokeInvite(wsId: number, inviteId: number): Promise<void> {
  await apiFetch<void>(`/settings/workspaces/${wsId}/invites/${inviteId}`, { method: 'DELETE' });
}

export async function removeMember(wsId: number, userId: number): Promise<void> {
  await apiFetch<void>(`/settings/workspaces/${wsId}/members/${userId}`, { method: 'DELETE' });
}

export async function updateMemberRole(
  wsId: number,
  userId: number,
  role: WorkspaceRole,
): Promise<MemberDto> {
  return apiFetch<MemberDto>(`/settings/workspaces/${wsId}/members/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

/**
 * People section → "Add to a workspace". Directly inserts a membership row for
 * an org member who already exists (no invite/accept round-trip) — the target
 * is already in the caller's organization, just not yet in this workspace.
 * `role` excludes 'owner': ownership is company-wide and only ever granted via
 * the org-members promote flow (`updateOrgMemberRole`), never per-workspace.
 */
export async function addMemberToWorkspace(
  wsId: number,
  userId: number,
  role: InvitableRole,
): Promise<MemberDto> {
  return apiFetch<MemberDto>(`/settings/workspaces/${wsId}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId, role }),
  });
}

export async function listOrgMembers(): Promise<OrgMemberDto[]> {
  return apiFetch<OrgMemberDto[]>('/settings/organization/members');
}

export async function updateOrgMemberRole(
  userId: number,
  body: UpdateOrgMemberRoleBody,
): Promise<OrgMemberDto> {
  return apiFetch<OrgMemberDto>(`/settings/organization/members/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Members screen → "Invite by email". No role argument: an invite from this screen
 * is ALWAYS an owner invite and the server fixes the role. Everything else is the
 * Team tab's `inviteMember` flow unchanged.
 */
export async function inviteOrgOwner(email: string): Promise<InviteDto> {
  return apiFetch<InviteDto>('/settings/organization/members/invite', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function removeOrgMember(userId: number): Promise<void> {
  await apiFetch<void>(`/settings/organization/members/${userId}`, {
    method: 'DELETE',
  });
}

export async function createShareableLink(
  wsId: number,
  role: InvitableRole,
): Promise<ShareableLinkDto> {
  return apiFetch<ShareableLinkDto>(`/settings/workspaces/${wsId}/invite-links`, {
    method: 'POST',
    body: JSON.stringify({ role }),
  });
}

export async function listShareableLinks(wsId: number): Promise<ShareableLinkDto[]> {
  return apiFetch<ShareableLinkDto[]>(`/settings/workspaces/${wsId}/invite-links`);
}

export async function revokeShareableLink(wsId: number, linkId: number): Promise<void> {
  await apiFetch<void>(`/settings/workspaces/${wsId}/invite-links/${linkId}`, { method: 'DELETE' });
}

// ── Quotas ──────────────────────────────────────────────────────────────────

export type QuotaDto = {
  workspaceId: number;
  workspaceName: string;
  dailyWarmupBudget: number;
};

export type QuotaListDto = {
  /**
   * The plan's `aggregateDailyWarmup` cap. `null` means UNLIMITED — never
   * coerce it to 0, which would read as "capped at nothing".
   */
  orgLimit: number | null;
  /**
   * Σ `dailyWarmupBudget` across active workspaces — the same quantity the
   * PATCH validates against `orgLimit`, so displayed headroom matches
   * enforced headroom.
   */
  allocated: number;
  workspaces: QuotaDto[];
};

export async function getQuotas(): Promise<QuotaListDto> {
  return apiFetch<QuotaListDto>('/settings/workspace-quota');
}

export type UpdateQuotaItem = { workspaceId: number; dailyWarmupBudget: number };

/**
 * Batch update — pass only the workspaces that actually changed, not every
 * workspace in the org. Returns one `QuotaDto` per requested workspace.
 */
export async function updateQuota(quotas: UpdateQuotaItem[]): Promise<QuotaDto[]> {
  return apiFetch<QuotaDto[]>('/settings/workspace-quota', {
    method: 'PATCH',
    body: JSON.stringify({ quotas }),
  });
}
